import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isNil, isValidDate } from '@pct-ts/typeguard';
import {
  isBefore,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfSecond,
  isAfter,
  isEqual,
  set,
} from 'date-fns';

/**
 * Két kontroll értékének összehasonlítása (pl. dátumok).
 * @param control1 - Az egyik FormControl
 * @param compareType - összehasonlítás típusa
 *   'greaterThen' | 'greaterOrEqual' ha control1-nek nagyobbnak / nagyobb vagy egyenlőnek kell lennie
 *   'lessThen' | 'lessOrEqual' ha control1-nek kisebbnek / kisebb vagy egyenlőnek kell lennie
 * @param control2 - A másik FormControl, amihez viszonyítunk
 * @param setErrorFor - Meghatározza, melyik kontroll(ok) kapjanak hibát
 * @param errorKey - (opcionális) egyedi kulcs a hibához, hogy több compareValidator ne ütközzön
 * @param precision - Dátum-összehasonlítás pontossága: a két dátum összevetése előtt mindkét érték erre a szintre
 * kerül normalizálásra (pl. 'day' esetén a nap elejére). Lehetséges értékek: 'day' | 'hour' | 'minute' | 'second' | 'millisecond'.
 * Alapértelmezett: 'millisecond' (nincs kerekítés).
 */
export function compareValidator(
  control1: AbstractControl,
  compareType: 'greaterThen' | 'greaterOrEqualTo' | 'lessThen' | 'lessOrEqualTo',
  control2: AbstractControl,
  setErrorFor: 'control1' | 'control2' | 'both' | 'none',
  errorKey = 'compareValidatorError',
  precision: DatePrecision = 'millisecond',
  onlyTime = false,
): ValidatorFn {
  return (): ValidationErrors | null => {
    const value1 = isValidDate(control1.value) ? control1.value : null;
    const value2 = isValidDate(control2.value) ? control2.value : null;

    if (isNil(value1) || isNil(value2)) {
      clearCompareError(control1, errorKey);
      clearCompareError(control2, errorKey);
      return null;
    }

    // Ha csak az időrészt kell figyelembe venni, akkor a dátumrész egységesítése
    const dateForCompare1 = onlyTime ? toTimeOfDay(value1) : value1;
    const dateForCompare2 = onlyTime ? toTimeOfDay(value2) : value2;

    const normalized1 = normalizeDate(dateForCompare1, precision);
    const normalized2 = normalizeDate(dateForCompare2, precision);

    let isValid: boolean;

    switch (compareType) {
      case 'greaterThen':
        isValid = isAfter(normalized1, normalized2);
        break;
      case 'greaterOrEqualTo':
        isValid = isAfter(normalized1, normalized2) || isEqual(normalized1, normalized2);
        break;
      case 'lessThen':
        isValid = isBefore(normalized1, normalized2);
        break;
      case 'lessOrEqualTo':
        isValid = isBefore(normalized1, normalized2) || isEqual(normalized1, normalized2);
        break;
      default:
        isValid = true;
    }

    updateErrors(control1, !isValid && ['control1', 'both'].includes(setErrorFor), errorKey);
    updateErrors(control2, !isValid && ['control2', 'both'].includes(setErrorFor), errorKey);

    return isValid ? null : { [errorKey]: true };
  };
}

function updateErrors(control: AbstractControl, shouldSetError: boolean, errorKey: string): void {
  if (!shouldSetError) {
    clearCompareError(control, errorKey);
    return;
  }

  const currentErrors = control.errors ?? {};
  control.setErrors({
    ...currentErrors,
    [errorKey]: true,
  });
}

function clearCompareError(control: AbstractControl, errorKey: string): void {
  if (!control.errors) {
    return;
  }
  const { [errorKey]: _, ...rest } = control.errors;
  control.setErrors(Object.keys(rest).length ? rest : null);
}

function normalizeDate(date: Date, precision: DatePrecision): Date {
  switch (precision) {
    case 'day':
      return startOfDay(date);
    case 'hour':
      return startOfHour(date);
    case 'minute':
      return startOfMinute(date);
    case 'second':
      return startOfSecond(date);
    case 'millisecond':
    default:
      return date;
  }
}

export type DatePrecision = 'day' | 'hour' | 'minute' | 'second' | 'millisecond';

function toTimeOfDay(date: Date): Date {
  return set(new Date(0), {
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
    milliseconds: date.getMilliseconds(),
  });
}
